import React, { useEffect, useState } from 'react';
import { BrokerageType } from '../../../../slices/marketplace/pricing/models/brokerage-type.enum';
import {
    FeeCalculatedTableValues,
    NullableFeeCalculatedTableRowValues,
} from '../FeeCalculatedTableValues';
import Helpers from '../../../../utils/helper';
import './FeeCalculatorResultTable.scss';
import { CurrencyFormatter } from '../../../../utils/currencyFormatter';
import InfoTooltip from '../../../../components/info-tooltip/info-tooltip';

interface FeeCalculatorResultTableRow
    extends NullableFeeCalculatedTableRowValues {
    rowName: string;
    type: 'currency' | 'percentage';
    tooltip?: string;
    isBold?: boolean;
}

interface FeeCalculatorResultTableProps {
    brokerageType: BrokerageType;
    table: FeeCalculatedTableValues;
    isFreeMember?: boolean;
}

const FREE_MEMBER_VISIBLE_ROWS = new Set(['Potential Sale Price', 'Transaction Fee']);

export const FeeCalculatorResultTable = (
    props: FeeCalculatorResultTableProps
) => {
    const [tableRows, setTableRows] = useState<FeeCalculatorResultTableRow[]>(
        []
    );

    useEffect(() => {
        const newTableRows: FeeCalculatorResultTableRow[] = [
            {
                rowName: 'Potential Sale Price',
                ...props.table.enterpriseValue,
                type: 'currency',
                tooltip:
                    'Fair market value of all purchase price consideration received',
                isBold: true,
            },
            {
                rowName: 'Application Fee',
                ...props.table.applicationFee,
                type: 'currency',
            },
            {
                rowName: 'Process Fees',
                ...props.table.processFees,
                type: 'currency',
            },
            {
                rowName: 'Transaction Fee',
                ...props.table.transactionFees,
                type: 'currency',
            },
            {
                rowName: 'Monthly Engagement Fees',
                ...props.table.monthlyRetainerFees,
                type: 'currency',
            },
            {
                rowName: 'Credits',
                ...props.table.credits,
                type: 'currency',
                tooltip:
                    'All credits received from the creditable portion of process fees and buyer meeting credits',
            },
            {
                rowName: 'Net Fees Paid at Closing',
                ...props.table.netFeesPaidAtClosing,
                type: 'currency',
            },
            {
                rowName: 'Total Fees Paid',
                ...props.table.totalFeesPaid,
                type: 'currency',
            },
            {
                rowName: 'Fees as % of Enterprise Value',
                ...props.table.feesAsPercentageOfEnterpriseValue,
                type: 'percentage',
                isBold: true,
            },
        ];
        setTableRows(newTableRows);
    }, [props]);

    return (
        <div className="fee-calculator-result-table">
            <h4 className="result-header">Total Deal Value Examples</h4>

            <table className="table">
                <tbody>
                    <tr className="table-header-container">
                        <td className="table-header">
                            {props.brokerageType === BrokerageType.Discount
                                ? 'Discount Brokerage'
                                : 'Full-Service Brokerage'}
                        </td>
                        <td className="absolute-td">
                            Transaction Fee:{' '}
                            {Helpers.GetCurrencyFormatWithDynamicCents(
                                props.table.txFee,
                                3,
                                undefined,
                                ''
                            )}
                            %
                        </td>
                    </tr>
                    {tableRows
                        .filter(
                            (t) => t.value1 !== undefined && t.value1 !== null
                        )
                        .filter(
                            (t) => !props.isFreeMember || FREE_MEMBER_VISIBLE_ROWS.has(t.rowName)
                        )
                        .map((row, index) => (
                            <tr
                                className={row.isBold ? 'row-bold' : ''}
                                key={index}>
                                <td>
                                    {row.rowName}
                                    {row.tooltip ? (
                                        <InfoTooltip
                                            text={row.tooltip}
                                            idText={`row-name-${index}`}
                                        />
                                    ) : (
                                        []
                                    )}
                                </td>
                                {[
                                    row.value1,
                                    row.value2,
                                    row.value3,
                                    row.value4,
                                    row.value5,
                                ].map((value, idx) => (
                                    <td
                                        key={idx}
                                        className={
                                            row.type === 'currency'
                                                ? 'currency-value'
                                                : 'percentage-value'
                                        }>
                                        {row.type === 'currency' ? (
                                            <span>
                                                {CurrencyFormatter.formatToDollarValue(
                                                    value
                                                )}
                                            </span>
                                        ) : (
                                            <span>
                                                {Helpers.GetCurrencyFormat(
                                                    value,
                                                    undefined,
                                                    undefined,
                                                    ''
                                                )}
                                                %
                                            </span>
                                        )}
                                    </td>
                                ))}
                            </tr>
                        ))}
                </tbody>
            </table>
        </div>
    );
};
